import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { LogIn, LogOut, X } from "lucide-react";
import { startPtLogin, ptLogout, fetchPtUser } from "./ptAuth";

// Small sign-in/account pill for the Poodle Talk family-facing UI.
// - Guest → "Sign in" button (subtle, doesn't disturb kids).
// - Signed in → tiny avatar + initial. Click → centered modal with email + sign out.
//
// Reads /api/poodletalk/auth/me once on mount and also listens for the
// "pt_auth_changed" custom event so the header refreshes after sign-in/out.
export default function PoodleTalkSignInPill({ compact = false }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      const u = await fetchPtUser();
      if (alive) {
        setUser(u);
        setLoading(false);
      }
    };
    refresh();

    const onChange = () => refresh();
    window.addEventListener("pt_auth_changed", onChange);
    return () => {
      alive = false;
      window.removeEventListener("pt_auth_changed", onChange);
    };
  }, []);

  // Close the modal on Escape — basic keyboard accessibility.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  if (loading) return null; // Avoid flashing the wrong state

  if (!user) {
    return (
      <button
        type="button"
        onClick={startPtLogin}
        data-testid="pt-signin-pill"
        className={`inline-flex items-center gap-1.5 bg-white/85 backdrop-blur-md text-[#4A4A4A] hover:text-[#A55B64] hover:bg-white px-2 sm:px-3 py-1.5 rounded-full text-xs font-medium shadow-md border border-white/60 transition-all ${
          compact ? "" : "sm:gap-2 sm:px-4 sm:text-sm"
        }`}
        title="Sign in to access the Poodle Talk App — your credits follow you on any network"
      >
        <LogIn className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        {/* Hide label on small screens to save header space — icon is enough */}
        <span className="hidden sm:inline">Sign in</span>
      </button>
    );
  }

  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();
  const avatarUrl = user.picture;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        data-testid="pt-account-pill"
        className="inline-flex items-center gap-1.5 bg-white/85 backdrop-blur-md hover:bg-white px-2 py-1 rounded-full shadow-md border border-white/60 transition-all"
        title="Signed in — tap to manage account"
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="w-7 h-7 rounded-full object-cover"
            referrerPolicy="no-referrer"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FFE5C2] to-[#FCEAEA] text-[#A55B64] flex items-center justify-center text-sm font-bold">
            {initial}
          </span>
        )}
      </button>
      {menuOpen && createPortal(
        <div
          data-testid="pt-account-menu-backdrop"
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
        >
          <div
            data-testid="pt-account-menu"
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-2xl shadow-2xl border border-[#E0E0E0] px-5 pt-4 pb-4 w-full max-w-xs max-h-[80vh] overflow-y-auto"
          >
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              data-testid="pt-account-menu-close"
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#F3F0E6] hover:bg-[#E5DFC7] text-[#6B6B6B] flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="flex flex-col items-center text-center">
              <p className="text-[10px] text-[#6B6B6B] uppercase tracking-wide mb-1 mt-1">Signed in as</p>
              <p className="text-sm font-semibold text-[#4A4A4A] break-words w-full px-6">{user.name || "Family"}</p>
              <p className="text-[11px] text-[#8A8A8A] break-all mb-3 w-full px-2">{user.email}</p>
              <p className="text-[10px] text-[#6B6B6B] mb-3 leading-snug px-1">
                Your chat credits travel with you on any network 💛
              </p>
              <button
                type="button"
                onClick={async () => {
                  await ptLogout();
                  setUser(null);
                  setMenuOpen(false);
                  window.dispatchEvent(new CustomEvent("pt_auth_changed"));
                }}
                data-testid="pt-signout-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FCEAEA] hover:bg-[#F5D4D4] text-[#A55B64] px-3 py-2 rounded-full text-xs font-medium transition-all"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
